import { useEffect, useRef, useState, useCallback } from 'react';
import { BASE_WS_URL } from '../config/config';

export default function useProfileSocket(userId) {
    const [profileData, setProfileData] = useState(null);
    const [error, setError] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [readyState, setReadyState] = useState(WebSocket.CLOSED);
    const token = localStorage.getItem('token');

    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const maxReconnectAttempts = 10;

    const userIdRef = useRef(userId);
    // Track the userId a given connect() call belongs to, so we only clear
    // profileData when the user actually changes — not on every reconnect.
    const lastUserIdRef = useRef(userId);

    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    const clearReconnectTimeout = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, []);

    const connect = useCallback(() => {
        if (!userIdRef.current) {
            return;
        }

        // Close existing socket if any
        if (socketRef.current?.readyState === WebSocket.OPEN ||
            socketRef.current?.readyState === WebSocket.CONNECTING) {
            socketRef.current.close(1000, 'New connection requested');
        }

        // IMPORTANT: profileData is NOT reset here on every connect/reconnect.
        // Doing that caused balance to flash: null -> stale Redux fallback
        // -> fresh socket value, every single time the socket reconnected
        // (network blips, backoff retries, StrictMode double-invoke, etc).
        // We only clear it when the userId itself actually changes (see
        // the effect below), so a reconnect just silently keeps showing
        // the last known-good balance until a fresh PROFILE_UPDATE arrives.

        clearReconnectTimeout();

        const socket = new WebSocket(`${BASE_WS_URL}/user/profile`);

        socketRef.current = socket;

        setReadyState(WebSocket.CONNECTING);
        setError(null);

        socket.onopen = () => {
            setIsConnected(true);
            setReadyState(WebSocket.OPEN);
            reconnectAttemptsRef.current = 0;

            const payload = {
                type: 'subscribe',
                userId: userIdRef.current,
            };

            socket.send(JSON.stringify(payload));
        };

        socket.onmessage = (event) => {
            try {
                const parsed = JSON.parse(event.data);

                if (parsed.type === 'PROFILE_UPDATE') {
                    // Merge: only overwrite fields the message actually sent,
                    // keep previous value for anything missing. Prevents a
                    // partial update from blanking balance/exp.
                    setProfileData((prev) => ({
                        balance:
                            parsed.data.balance !== undefined
                                ? parsed.data.balance
                                : prev?.balance,
                        exp:
                            parsed.data.exp !== undefined
                                ? parsed.data.exp
                                : prev?.exp,
                    }));
                } else if (parsed.type === 'FORCE_LOGOUT') {
                    if (socketRef.current) {
                        socketRef.current.close(1000, 'Force logout');
                        socketRef.current = null;
                    }
                    clearReconnectTimeout();
                    localStorage.clear();
                    window.location.href = '/login';
                }
            } catch (err) {
                // ignore malformed message
            }
        };

        socket.onerror = () => {
            setError('WebSocket connection error occurred');
        };

        socket.onclose = (event) => {
            setIsConnected(false);
            setReadyState(WebSocket.CLOSED);
            socketRef.current = null;

            // Do not reconnect on normal closure (1000)
            if (event.code === 1000) {
                setError(null);
                return;
            }

            // Reconnect with exponential backoff (only if under max attempts)
            if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                const delay = Math.min(2000 * Math.pow(1.5, reconnectAttemptsRef.current), 30000);
                reconnectAttemptsRef.current += 1;

                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, delay);
            } else {
                setError('Failed to connect to profile service after multiple attempts');
            }
        };
    }, [clearReconnectTimeout]);

    useEffect(() => {
        if (userId) {
            // Only clear stale data when the user actually changed
            // (login as a different account). A reconnect for the SAME
            // user should never blank out the last known balance.
            if (lastUserIdRef.current !== userId) {
                setProfileData(null);
            }
            lastUserIdRef.current = userId;
            connect();
        } else {
            // Cleanup when no userId (logout)
            if (socketRef.current) {
                socketRef.current.close(1000, 'userId removed');
            }
            setProfileData(null);
            lastUserIdRef.current = null;
        }

        // Cleanup on unmount / userId change
        return () => {
            clearReconnectTimeout();
            if (socketRef.current) {
                socketRef.current.close(1000, 'component unmount');
                socketRef.current = null;
            }
        };
    }, [userId, connect, clearReconnectTimeout]);

    const refresh = useCallback(() => {
        const socket = socketRef.current;
        if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({
                type: 'refresh',
                userId: userIdRef.current,
            }));
        }
    }, []);

    const reconnect = useCallback(() => {
        reconnectAttemptsRef.current = 0;
        connect();
    }, [connect]);

    return {
        profileData,
        error,
        isConnected,
        readyState,
        refresh,
        reconnect,
    };
}